/* ============================================================
   VIDEO BANNER CUSTOM — JavaScript Controller
   ============================================================ */

(function () {
  'use strict';

  function pad(n) {
    return String(Math.floor(n)).padStart(2, '0');
  }

  function formatTime(seconds) {
    if (isNaN(seconds) || seconds < 0) return '0:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    if (h > 0) return h + ':' + pad(m) + ':' + pad(s);
    return m + ':' + pad(s);
  }

  class VideoBannerCustom {
    constructor(section) {
      this.section = section;
      this.wrapper  = section.querySelector('.vbc-wrapper');
      this.video    = section.querySelector('.vbc-video');

      if (!this.video) return;

      this.btnPlay      = section.querySelector('.vbc-btn-play');
      this.btnMute      = section.querySelector('.vbc-btn-mute');
      this.btnFullscreen = section.querySelector('.vbc-btn-fullscreen');
      this.progressTrack = section.querySelector('.vbc-progress-track');
      this.progressFill  = section.querySelector('.vbc-progress-fill');
      this.progressBuf   = section.querySelector('.vbc-progress-buffer');
      this.volSlider     = section.querySelector('.vbc-volume-slider');
      this.timeEl        = section.querySelector('.vbc-time');

      this._dragging = false;
      this._controlTimer = null;

      this._bind();
      this._syncMuteIcon();
      this._syncPlayIcon();
    }

    /* ── Icon helpers ── */
    _iconPlay() {
      return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 5v14l11-7z"/>
      </svg>`;
    }
    _iconPause() {
      return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
      </svg>`;
    }
    _iconVolume() {
      return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
      </svg>`;
    }
    _iconMute() {
      return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
      </svg>`;
    }
    _iconFullscreen() {
      return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
      </svg>`;
    }
    _iconExitFullscreen() {
      return `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
      </svg>`;
    }

    _syncPlayIcon() {
      if (!this.btnPlay) return;
      const paused = this.video.paused;
      this.btnPlay.innerHTML = paused ? this._iconPlay() : this._iconPause();
      this.wrapper.classList.toggle('is-paused', paused);
    }

    _syncMuteIcon() {
      if (!this.btnMute) return;
      this.btnMute.innerHTML = this.video.muted ? this._iconMute() : this._iconVolume();
    }

    _syncProgress() {
      if (this._dragging || !this.video.duration) return;
      const pct = (this.video.currentTime / this.video.duration) * 100;
      if (this.progressFill) this.progressFill.style.width = pct + '%';
      if (this.timeEl) {
        this.timeEl.textContent = formatTime(this.video.currentTime) + ' / ' + formatTime(this.video.duration);
      }
    }

    _syncBuffer() {
      if (!this.video.duration || !this.progressBuf) return;
      const buf = this.video.buffered;
      if (buf.length > 0) {
        const end = buf.end(buf.length - 1);
        this.progressBuf.style.width = (end / this.video.duration) * 100 + '%';
      }
    }

    _seekFromEvent(e) {
      const rect = this.progressTrack.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      this.video.currentTime = ratio * this.video.duration;
      if (this.progressFill) this.progressFill.style.width = (ratio * 100) + '%';
      if (this.timeEl) {
        this.timeEl.textContent = formatTime(this.video.currentTime) + ' / ' + formatTime(this.video.duration);
      }
    }

    _showControls() {
      this.wrapper.classList.add('show-controls');
      clearTimeout(this._controlTimer);
      this._controlTimer = setTimeout(() => {
        if (!this.video.paused) {
          this.wrapper.classList.remove('show-controls');
        }
      }, 3000);
    }

    _bind() {
      // Play / Pause via click on wrapper
      this.wrapper.addEventListener('click', (e) => {
        // Don't toggle if clicking on controls
        if (e.target.closest('.vbc-controls')) return;
        this._togglePlay();
      });

      // Play button
      if (this.btnPlay) {
        this.btnPlay.addEventListener('click', (e) => {
          e.stopPropagation();
          this._togglePlay();
        });
      }

      // Mute button
      if (this.btnMute) {
        this.btnMute.addEventListener('click', (e) => {
          e.stopPropagation();
          this.video.muted = !this.video.muted;
          if (this.volSlider) this.volSlider.value = this.video.muted ? 0 : this.video.volume;
          this._syncMuteIcon();
        });
      }

      // Volume slider
      if (this.volSlider) {
        this.volSlider.addEventListener('input', (e) => {
          this.video.volume = parseFloat(e.target.value);
          this.video.muted = this.video.volume === 0;
          this._syncMuteIcon();
        });
        // Set initial value
        this.volSlider.value = this.video.muted ? 0 : this.video.volume;
      }

      // Fullscreen
      if (this.btnFullscreen) {
        this.btnFullscreen.addEventListener('click', (e) => {
          e.stopPropagation();
          if (!document.fullscreenElement) {
            this.wrapper.requestFullscreen && this.wrapper.requestFullscreen();
            this.btnFullscreen.innerHTML = this._iconExitFullscreen();
          } else {
            document.exitFullscreen && document.exitFullscreen();
            this.btnFullscreen.innerHTML = this._iconFullscreen();
          }
        });
        document.addEventListener('fullscreenchange', () => {
          if (!document.fullscreenElement) {
            this.btnFullscreen.innerHTML = this._iconFullscreen();
          }
        });
      }

      // Progress — mouse
      if (this.progressTrack) {
        this.progressTrack.addEventListener('mousedown', (e) => {
          this._dragging = true;
          this._seekFromEvent(e);
        });
        document.addEventListener('mousemove', (e) => {
          if (this._dragging) this._seekFromEvent(e);
        });
        document.addEventListener('mouseup', () => {
          this._dragging = false;
        });

        // Progress — touch
        this.progressTrack.addEventListener('touchstart', (e) => {
          this._dragging = true;
          this._seekFromEvent(e);
        }, { passive: true });
        document.addEventListener('touchmove', (e) => {
          if (this._dragging) this._seekFromEvent(e);
        }, { passive: true });
        document.addEventListener('touchend', () => {
          this._dragging = false;
        });
      }

      // Video events
      this.video.addEventListener('timeupdate', () => this._syncProgress());
      this.video.addEventListener('progress', () => this._syncBuffer());
      this.video.addEventListener('play', () => this._syncPlayIcon());
      this.video.addEventListener('pause', () => this._syncPlayIcon());
      this.video.addEventListener('volumechange', () => this._syncMuteIcon());
      this.video.addEventListener('loadedmetadata', () => {
        this._syncProgress();
        if (this.volSlider) this.volSlider.value = this.video.muted ? 0 : this.video.volume;
      });

      // Show controls on mousemove / touch
      this.wrapper.addEventListener('mousemove', () => this._showControls());
      this.wrapper.addEventListener('touchstart', () => this._showControls(), { passive: true });
    }

    _togglePlay() {
      if (this.video.paused) {
        this.video.play();
      } else {
        this.video.pause();
      }
    }
  }

  // Init on DOMContentLoaded and when Shopify re-renders sections
  function initAll() {
    document.querySelectorAll('.vbc-section').forEach((section) => {
      if (!section._vbcInit) {
        section._vbcInit = new VideoBannerCustom(section);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target;
    if (section.querySelector('.vbc-section')) {
      section._vbcInit = null;
      initAll();
    }
  });
})();
